// מסך 10 — אשף חילוץ שפה גרפית מאתר, PDF ותמונות → סקירה ואישור → גרסת מותג / שפה מקומית לקמפיין.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, waitForJob, type Job } from '../api';
import { ErrorBox, Field, Loading, fmtKb, useLoad, useMe } from '../components/ui';
import { JobProgress, PageHead, Tabs, errMsg, useAction } from '../components/mgmt/common';
import { isUrl } from '../components/mgmt/LinksEditor';
import { ColorCandidates, ConflictList, FontCandidates, LogoCandidates, OtherCandidates, useCandidateActions, type Candidate, type Extraction } from '../components/mgmt/BrandReview';
import type { BrandRow } from '../components/mgmt/BrandPanel';

type Mode = 'new_brand' | 'new_version' | 'campaign_local';
type Step = 'sources' | 'running' | 'review' | 'done';
interface ClientFull { client: { id: string; name: string }; brands: BrandRow[]; campaigns: { id: string; name: string }[] }

export default function BrandImport() {
  const [sp, setSp] = useSearchParams();
  const clientId = sp.get('clientId');
  const me = useMe();
  const clients = useLoad<{ id: string; name: string }[]>(clientId ? null : '/api/clients');
  const client = useLoad<ClientFull>(clientId ? `/api/clients/${clientId}` : null);
  const [extractionId, setExtractionId] = useState<string | null>(sp.get('extractionId'));
  const [step, setStep] = useState<Step>(sp.get('extractionId') ? 'review' : 'sources');
  const [job, setJob] = useState<Job | null>(null);
  const [mode, setMode] = useState<Mode>(sp.get('campaignId') ? 'campaign_local' : 'new_brand');
  const [brandName, setBrandName] = useState('');
  const [targetBrandId, setTargetBrandId] = useState('');
  const [campaignId, setCampaignId] = useState(sp.get('campaignId') ?? '');
  const [committed, setCommitted] = useState<any>(null);

  if (me.role === 'viewer') return <div className="alert warn">חילוץ שפה גרפית זמין לעורכים בלבד.</div>;

  if (!clientId) {
    return (
      <div>
        <PageHead title="חילוץ שפה גרפית" sub="בחרו לקוח. המקורות והממצאים נשמרים אצל הלקוח." />
        <ErrorBox error={clients.error} />
        {clients.loading && <Loading />}
        <div className="card" style={{ maxWidth: 420 }}>
          <Field label="לקוח"><select defaultValue="" onChange={(e) => e.target.value && setSp({ clientId: e.target.value })}><option value="">בחרו…</option>{(clients.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        </div>
      </div>
    );
  }
  const c = client.data;
  return (
    <div>
      <PageHead title="חילוץ שפה גרפית" crumbs={c ? <><Link to="/clients">לקוחות</Link> / <Link to={`/clients/${clientId}`}>{c.client.name}</Link> / חילוץ שפה גרפית</> : undefined}
        sub="תהליך נפרד מחילוץ הוראות סגירה של מדיה. הממצאים הם הצעות — שום דבר לא נשמר למותג בלי אישור, ומותג קיים לא נדרס." />
      <ol className="row small" aria-label="שלבים" style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
        {(['sources', 'running', 'review', 'done'] as Step[]).map((s, i) => (
          <li key={s} className={`badge ${step === s ? 'info' : ''}`} aria-current={step === s ? 'step' : undefined}>{i + 1}. {{ sources: 'מקורות ויעד', running: 'עיבוד', review: 'סקירה ואישור', done: 'נשמר' }[s]}</li>
        ))}
      </ol>
      <ErrorBox error={client.error} />
      {!c && client.loading && <Loading />}
      {c && step === 'sources' && (
        <SourcesForm client={c} mode={mode} setMode={setMode} brandName={brandName} setBrandName={setBrandName} targetBrandId={targetBrandId} setTargetBrandId={setTargetBrandId}
          campaignId={campaignId} setCampaignId={setCampaignId}
          onStarted={(eid, jobId) => {
            setExtractionId(eid); setStep('running'); setJob(null);
            setSp((p) => { p.set('extractionId', eid); return p; }, { replace: true });
            waitForJob(jobId, setJob).then((j) => { setJob(j); if (j.status === 'succeeded') setStep('review'); }).catch((e) => setJob({ id: jobId, type: 'brand_extract', status: 'failed', result: null, error: errMsg(e), progress: null }));
          }} />
      )}
      {step === 'running' && (
        <div className="card stack">
          <JobProgress job={job} busy={!job} label="חילוץ מהמקורות" />
          {job?.status === 'failed' && (
            <div className="alert err stack">
              <strong>החילוץ נכשל.</strong>
              <span>אם האתר חסום, דורש כניסה או לא נגיש — אין עקיפה. אפשר להעלות קובצי PDF או תמונות במקום.</span>
              <div className="row"><button className="btn" onClick={() => setStep('sources')}>חזרה להעלאת קבצים</button>{extractionId && <button className="btn" onClick={() => setStep('review')}>הצגת מה שנמצא בכל זאת</button>}</div>
            </div>
          )}
        </div>
      )}
      {c && step === 'review' && extractionId && (
        <Review extractionId={extractionId} client={c} mode={mode} setMode={setMode} brandName={brandName} setBrandName={setBrandName} campaignId={campaignId} setCampaignId={setCampaignId}
          onBack={() => setStep('sources')} onCommitted={(r) => { setCommitted(r); setStep('done'); }} />
      )}
      {step === 'done' && (
        <div className="card stack" role="status" aria-live="polite">
          <div className="alert ok">{mode === 'campaign_local' ? 'השפה הגרפית נשמרה כשפה מקומית לקמפיין. מותג הלקוח לא השתנה.' : 'נוצרה גרסת מותג חדשה מהרכיבים המאושרים.'}</div>
          <div className="row">
            {mode === 'campaign_local' && (committed?.campaignId ?? campaignId) && <Link className="btn primary" to={`/campaigns/${committed?.campaignId ?? campaignId}?tab=look`}>לקמפיין</Link>}
            {mode !== 'campaign_local' && <Link className="btn primary" to={`/clients/${clientId}?tab=brands`}>למותגי הלקוח{committed?.version ? ` (נוצרה גרסה ${committed.version})` : ''}</Link>}
            <button className="btn" onClick={() => { setStep('sources'); setExtractionId(null); setSp({ clientId }); }}>חילוץ נוסף</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SourcesForm(p: {
  client: ClientFull; mode: Mode; setMode: (m: Mode) => void; brandName: string; setBrandName: (s: string) => void; targetBrandId: string; setTargetBrandId: (s: string) => void;
  campaignId: string; setCampaignId: (s: string) => void; onStarted: (extractionId: string, jobId: string) => void;
}) {
  const [siteUrl, setSiteUrl] = useState('');
  const [pages, setPages] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [preferred, setPreferred] = useState<number>(0);
  const { busy, error, run } = useAction();
  const host = (() => { try { return new URL(siteUrl).hostname.replace(/^www\./, ''); } catch { return null; } })();
  const pageErrors = pages.map((u) => {
    if (!u) return null;
    if (!isUrl(u)) return 'כתובת לא תקינה';
    try { if (host && new URL(u).hostname.replace(/^www\./, '') !== host) return 'רק עמודים באותו דומיין'; } catch { /* */ }
    return null;
  });
  const siteBad = siteUrl !== '' && !isUrl(siteUrl);
  const httpWarn = siteUrl.startsWith('http://');
  const sourcesList = [...(siteUrl ? [{ label: `אתר: ${siteUrl}` }] : []), ...pages.filter(Boolean).map((u) => ({ label: `עמוד: ${u}` })), ...files.map((f) => ({ label: `קובץ: ${f.name}` }))];
  useEffect(() => { if (preferred >= sourcesList.length) setPreferred(0); }, [sourcesList.length, preferred]);
  const targetOk = p.mode === 'new_brand' ? !!p.brandName.trim() : p.mode === 'new_version' ? !!p.targetBrandId : !!p.campaignId;
  const ready = sourcesList.length > 0 && !siteBad && pageErrors.every((x) => !x) && targetOk;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fd = new FormData();
    fd.set('clientId', p.client.client.id);
    if (p.mode === 'new_brand') fd.set('newBrandName', p.brandName.trim());
    if (p.mode === 'new_version') fd.set('targetBrandId', p.targetBrandId);
    if (p.mode === 'campaign_local') fd.set('campaignId', p.campaignId);
    fd.set('urls', JSON.stringify([siteUrl, ...pages].filter(Boolean)));
    fd.set('preferred', String(preferred));
    for (const f of files) fd.append('files', f, f.name);
    const r = await run(() => api.upload<{ extractionId: string; jobId: string }>('/api/brands/import', fd));
    if (r) p.onStarted(r.extractionId, r.jobId);
  };

  return (
    <form className="grid cols-2" onSubmit={submit} style={{ alignItems: 'start' }}>
      <section className="card stack" aria-labelledby="src-h">
        <h2 id="src-h" style={{ margin: 0 }}>מקורות</h2>
        <Field label="אתר הפרויקט (עמוד ראשי)" hint="עמוד ציבורי בלבד, https. אין שמירת סיסמאות או עקיפת כניסה."><input className="ltr" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://" aria-invalid={siteBad} /></Field>
        {siteBad && <div className="small" style={{ color: 'var(--danger)' }}>כתובת לא תקינה</div>}
        {httpWarn && <div className="small" style={{ color: 'var(--warn)' }}>קישורי HTTPS בלבד כברירת מחדל — כתובת http עלולה להידחות.</div>}
        <fieldset className="box" disabled={!host}>
          <legend>עמודים נוספים באותו דומיין (עד 5)</legend>
          {pages.map((u, i) => (
            <div key={i} className="stack" style={{ gap: 2, marginBottom: 4 }}>
              <div className="row"><input aria-label={`עמוד ${i + 1}`} className="ltr grow" value={u} onChange={(e) => setPages(pages.map((x, j) => (j === i ? e.target.value : x)))} placeholder={host ? `https://${host}/...` : ''} />
                <button type="button" className="btn small danger" onClick={() => setPages(pages.filter((_, j) => j !== i))}>הסרה</button></div>
              {pageErrors[i] && <span className="small" style={{ color: 'var(--danger)' }}>{pageErrors[i]}</span>}
            </div>
          ))}
          <button type="button" className="btn small" disabled={pages.length >= 5} onClick={() => setPages([...pages, ''])}>הוספת עמוד</button>
        </fieldset>
        <Field label="קובצי PDF ותמונות (אפשר כמה יחד)"><input type="file" multiple accept="application/pdf,image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => setFiles([...(e.target.files ?? [])])} /></Field>
        {files.length > 0 && <div className="small muted">{files.length} קבצים · {fmtKb(files.reduce((s, f) => s + f.size, 0))}</div>}
        {sourcesList.length > 1 && (
          <fieldset className="box">
            <legend>מקור מועדף (בסתירה — מוצג ראשון)</legend>
            {sourcesList.map((s, i) => <label key={i} className="checkline"><input type="radio" name="preferred" checked={preferred === i} onChange={() => setPreferred(i)} /> <span className="ltr" style={{ overflowWrap: 'anywhere' }}>{s.label}</span></label>)}
          </fieldset>
        )}
      </section>
      <section className="card stack" aria-labelledby="tgt-h">
        <h2 id="tgt-h" style={{ margin: 0 }}>יעד</h2>
        <label className="checkline"><input type="radio" name="mode" checked={p.mode === 'new_brand'} onChange={() => p.setMode('new_brand')} /> מותג חדש ללקוח</label>
        {p.mode === 'new_brand' && <Field label="שם המותג החדש"><input value={p.brandName} onChange={(e) => p.setBrandName(e.target.value)} /></Field>}
        <label className="checkline"><input type="radio" name="mode" checked={p.mode === 'new_version'} onChange={() => p.setMode('new_version')} disabled={!p.client.brands.length} /> הצעת גרסה חדשה למותג קיים</label>
        {p.mode === 'new_version' && <Field label="מותג"><select value={p.targetBrandId} onChange={(e) => p.setTargetBrandId(e.target.value)}><option value="">בחרו…</option>{p.client.brands.map((b) => <option key={b.id} value={b.id}>{b.name} (גרסה {b.current_version})</option>)}</select></Field>}
        <label className="checkline"><input type="radio" name="mode" checked={p.mode === 'campaign_local'} onChange={() => p.setMode('campaign_local')} disabled={!p.client.campaigns.length} /> שפה מקומית לקמפיין (לא משנה את מותג הלקוח)</label>
        {p.mode === 'campaign_local' && <Field label="קמפיין"><select value={p.campaignId} onChange={(e) => p.setCampaignId(e.target.value)}><option value="">בחרו…</option>{p.client.campaigns.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select></Field>}
        <div className="alert info small">לוגו אינו נוצר או משוחזר ב-AI. כשאין מקור נקי תתבקשו להעלות קובץ או לאשר חיתוך באיכות המקור.</div>
        <ErrorBox error={error} />
        <div><button className="btn primary" disabled={busy || !ready}>{busy ? 'שולח…' : 'התחלת חילוץ'}</button></div>
        {!sourcesList.length && <div className="small muted">יש להזין אתר או לבחור לפחות קובץ אחד.</div>}
      </section>
    </form>
  );
}

type RTab = 'logos' | 'colors' | 'fonts' | 'other' | 'conflicts';
function Review(p: { extractionId: string; client: ClientFull; mode: Mode; setMode: (m: Mode) => void; brandName: string; setBrandName: (s: string) => void; campaignId: string; setCampaignId: (s: string) => void; onBack: () => void; onCommitted: (r: any) => void }) {
  const { data, error, loading, setData, reload } = useLoad<Extraction>(`/api/brand-extractions/${p.extractionId}`);
  const [tab, setTab] = useState<RTab>('logos');
  const update = (c: Candidate) => { if (data) setData({ ...data, candidates: data.candidates.map((x) => (x.id === c.id ? { ...x, ...c } : x)) }); };
  const actions = useCandidateActions(update);
  const commit = useAction();
  const by = useMemo(() => {
    const cs = data?.candidates ?? [];
    return { logos: cs.filter((c) => c.kind === 'logo'), colors: cs.filter((c) => c.kind === 'color'), fonts: cs.filter((c) => c.kind === 'font'), other: cs.filter((c) => !['logo', 'color', 'font'].includes(c.kind)) };
  }, [data]);
  useEffect(() => {
    const e = data?.extraction;
    if (!e) return;
    if (e.campaign_id && !p.campaignId) { p.setMode('campaign_local'); p.setCampaignId(e.campaign_id); }
    else if (e.target_brand_id && p.mode === 'new_brand' && !p.brandName) p.setMode('new_version');
    if (e.new_brand_name && !p.brandName) p.setBrandName(e.new_brand_name);
  }, [data?.extraction?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) return <Loading />;
  if (error && !data) return <div className="stack"><ErrorBox error={error} /><div><button className="btn" onClick={p.onBack}>חזרה</button></div></div>;
  if (!data) return null;
  const approved = data.candidates.filter((c) => c.status === 'approved');
  const srcErrs = data.sourceErrors ?? [];
  const failedSources = data.sources.filter((s) => s.status === 'failed' || srcErrs.some((e) => e.sourceId === s.id))
    .map((s) => ({ ...s, error: s.error ?? srcErrs.find((e) => e.sourceId === s.id)?.error ?? null }));
  const doCommit = async () => {
    const body = { mode: p.mode, brandId: p.mode === 'new_version' ? data.extraction.target_brand_id ?? undefined : undefined, brandName: p.mode === 'new_brand' ? p.brandName || data.extraction.new_brand_name || undefined : undefined, campaignId: p.mode === 'campaign_local' ? p.campaignId : undefined };
    const r = await commit.run(() => api.post(`/api/brand-extractions/${p.extractionId}/commit`, body), 'נשמר');
    if (r) p.onCommitted(r);
  };
  const cnt = (xs: Candidate[]) => `${xs.filter((x) => x.status === 'approved').length}/${xs.length}`;
  return (
    <div className="stack">
      {failedSources.length > 0 && (
        <div className="alert err" role="alert">
          <strong>מקורות שנכשלו:</strong>
          <ul style={{ margin: '4px 0' }}>{failedSources.map((s) => <li key={s.id}><span className="ltr">{s.url ?? s.name ?? s.id}</span> — {s.error ?? 'כשל'}</li>)}</ul>
          אתר חסום או שדורש כניסה לא נסרק (אין עקיפה). <button className="btn small" onClick={p.onBack}>העלאת קבצים במקום</button>
        </div>
      )}
      {(data.warnings ?? []).length > 0 && <div className="alert warn small"><ul style={{ margin: 0 }}>{(data.warnings ?? []).map((w, i) => <li key={i}>{typeof w === 'string' ? w : JSON.stringify(w)}</li>)}</ul></div>}
      <div className="card row between">
        <div className="small">מקורות: {data.sources.map((s) => <span key={s.id} className={`badge ${s.status === 'failed' ? 'err' : s.preferred ? 'info' : ''}`} style={{ marginInlineEnd: 4 }}>{s.kind}{s.preferred ? ' · מועדף' : ''}</span>)}</div>
        <button className="btn small" onClick={reload}>רענון</button>
      </div>
      <Tabs<RTab> label="רכיבים שנמצאו" value={tab} onChange={setTab} tabs={[
        { id: 'logos', label: `לוגו (${cnt(by.logos)})` }, { id: 'colors', label: `צבעים (${cnt(by.colors)})` }, { id: 'fonts', label: `פונטים (${cnt(by.fonts)})` },
        { id: 'other', label: `חומרים וסגנון (${cnt(by.other)})` }, { id: 'conflicts', label: `סתירות (${data.conflicts.length})` },
      ]} />
      <div role="tabpanel" className="card" aria-live="polite">
        <ErrorBox error={actions.error} />
        {tab === 'logos' && <LogoCandidates items={by.logos} sources={data.sources} actions={actions} />}
        {tab === 'colors' && <ColorCandidates items={by.colors} sources={data.sources} actions={actions} />}
        {tab === 'fonts' && <FontCandidates items={by.fonts} sources={data.sources} actions={actions} />}
        {tab === 'other' && <OtherCandidates items={by.other} sources={data.sources} actions={actions} />}
        {tab === 'conflicts' && <ConflictList conflicts={data.conflicts} candidates={data.candidates} actions={actions} />}
      </div>
      <section className="card stack" aria-labelledby="commit-h">
        <h2 id="commit-h" style={{ margin: 0 }}>שמירה</h2>
        <div className="row">
          <label className="checkline"><input type="radio" name="cmode" checked={p.mode === 'new_brand'} onChange={() => p.setMode('new_brand')} disabled={!!data.extraction.target_brand_id} /> מותג חדש</label>
          <label className="checkline"><input type="radio" name="cmode" checked={p.mode === 'new_version'} onChange={() => p.setMode('new_version')} disabled={!data.extraction.target_brand_id} /> גרסה חדשה למותג שנבחר</label>
          <label className="checkline"><input type="radio" name="cmode" checked={p.mode === 'campaign_local'} onChange={() => p.setMode('campaign_local')} disabled={!p.client.campaigns.length} /> שפה מקומית לקמפיין</label>
        </div>
        {p.mode === 'new_brand' && <Field label="שם המותג"><input value={p.brandName} onChange={(e) => p.setBrandName(e.target.value)} /></Field>}
        {p.mode === 'campaign_local' && <Field label="קמפיין"><select value={p.campaignId} onChange={(e) => p.setCampaignId(e.target.value)}><option value="">בחרו…</option>{p.client.campaigns.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select></Field>}
        <div className="small">יישמרו {approved.length} רכיבים מאושרים. {by.logos.length > 0 && !by.logos.some((l) => l.status === 'approved') && <span style={{ color: 'var(--warn)' }}>לא אושר לוגו.</span>} {data.conflicts.length > 0 && <span style={{ color: 'var(--warn)' }}>יש {data.conflicts.length} סתירות — ודאו שבחרתם את הערך הנכון.</span>}</div>
        <ErrorBox error={commit.error} />
        <div className="row">
          <button className="btn primary" onClick={doCommit} disabled={commit.busy || !approved.length || (p.mode === 'new_brand' && !p.brandName && !data.extraction.new_brand_name) || (p.mode === 'campaign_local' && !p.campaignId)}>
            {commit.busy ? 'שומר…' : p.mode === 'campaign_local' ? 'שמירה כשפה מקומית' : 'יצירת גרסת מותג מהמאושרים'}
          </button>
          <button className="btn" onClick={p.onBack}>הוספת מקורות</button>
        </div>
      </section>
    </div>
  );
}
